import { Badge, Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import type { PlanoDeSaude } from '@/data/planosDeSaude';

export interface DetalheDoPlanoModalProps {
    /** Nulo quando o `?plano=` da URL não é desta clínica. */
    plano: PlanoDeSaude | null;
    podeEditar: boolean;
    onFechar: () => void;
    onEditar: (plano: PlanoDeSaude) => void;
}

/**
 * Detalhe do plano. Atendimentos e receita por plano, que o protótipo mostrava,
 * não entram: eram números fixos, e o atendimento ainda não guarda o plano.
 */
export function DetalheDoPlanoModal({ plano, podeEditar, onFechar, onEditar }: DetalheDoPlanoModalProps) {
    const linhas: [string, string][] = plano ? [
        ['Registro na ANS', plano.registroAns ?? '—'],
        ['Tabela de preços', plano.tabelaDePrecos ?? '—'],
        ['Repasse', plano.repassePercentual === undefined ? '—' : `${plano.repassePercentual.toLocaleString('pt-BR')}%`],
        ['Prazo de pagamento', plano.prazoRecebimento ?? '—'],
        ['Carência', plano.carencia ?? (plano.tipo === 'Convênio' ? 'Sem carência' : '—')],
        ['Pacientes com este plano', String(plano.pacientes)],
    ] : [];

    return (
        <Modal
            open width={560} onClose={onFechar}
            title={plano?.nome ?? 'Plano de saúde'}
            description={plano ? [plano.tipo, plano.operadora].filter(Boolean).join(' · ') : undefined}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Fechar</Button>
                    {plano && podeEditar && (
                        <Button iconLeft={<Icon name="pencil" size={15} />} onClick={() => onEditar(plano)}>Editar plano</Button>
                    )}
                </>
            )}
        >
            {!plano ? (
                <Alert tone="danger" title="Plano não encontrado" dismissible={false}>
                    O endereço aponta para um plano que não existe nesta clínica.
                </Alert>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 4, background: plano.cor }} />
                        <Badge tone={plano.ativo ? 'success' : 'neutral'} dot>{plano.ativo ? 'Ativo' : 'Inativo'}</Badge>
                    </div>
                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '9px 16px', fontFamily: 'var(--font-ui)', fontSize: 13 }}>
                        {linhas.map(([rotulo, valor]) => (
                            <div key={rotulo} style={{ display: 'contents' }}>
                                <dt style={{ color: 'var(--text-secondary)' }}>{rotulo}</dt>
                                <dd style={{ margin: 0, textAlign: 'right', fontWeight: 600, color: 'var(--text-primary)' }}>{valor}</dd>
                            </div>
                        ))}
                    </dl>
                    {plano.observacoes && (
                        <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-body)' }}>{plano.observacoes}</p>
                    )}
                    {plano.tipo === 'Convênio' && plano.repassePercentual !== undefined && plano.repassePercentual < 70 && (
                        <Alert tone="warning" title="Repasse abaixo de 70%" dismissible={false}>
                            Confira a margem por procedimento antes de ampliar o volume deste convênio.
                        </Alert>
                    )}
                </div>
            )}
        </Modal>
    );
}
